/**
 * Pop a piece of the workspace into its own browser window.
 *
 * The popped content stays in THIS React tree — it is portaled into the
 * child window's document — so it shares the parent's context, handlers and
 * state. An edit made in the popout is an edit in the parent, with nothing
 * to sync; the parent grid re-renders on the same tick.
 *
 * Lifecycle / guards:
 *  - the child window mirrors the parent's stylesheets (and keeps mirroring
 *    as the parent's <head> changes) and theme attributes;
 *  - closing the child window calls `onClosed` so the parent docks the pane;
 *  - when the parent unmounts the popout (user navigated away, version
 *    closed, tab switched) the child is BLOCKED: the React content is gone
 *    and a static overlay says the workspace is no longer open, with a
 *    button to close the window. It never keeps editable UI alive without a
 *    parent to write through;
 *  - the parent's `pagehide` (navigation / refresh / tab close) closes the
 *    child outright.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Wide content (the Zero Base / Labor Config modals) asks the popout to grow
 * while it is open; the window shrinks back to what the user had when it
 * closes. Docked in the main window this is a no-op.
 */
interface PopoutSizeApi {
  requestWide: (minWidth: number, minHeight: number) => () => void;
}
const PopoutSizeContext = createContext<PopoutSizeApi | null>(null);

export function useWideContent(active: boolean, minWidth = 1100, minHeight = 820) {
  const api = useContext(PopoutSizeContext);
  useEffect(() => {
    if (!active || !api) return;
    return api.requestWide(minWidth, minHeight);
  }, [active, api, minWidth, minHeight]);
}

export interface PopoutWindowProps {
  open: boolean;
  title: string;
  width?: number;
  height?: number;
  /** Child window was closed by the user (or blocked). */
  onClosed: () => void;
  /** Shown in the child when nothing is rendered (e.g. no selection), instead of an empty page. */
  emptyState?: React.ReactNode;
  children?: React.ReactNode;
}

const BLOCK_HTML = `
  <div style="position:fixed;inset:0;display:flex;align-items:center;justify-content:center;background:#F6F6FA;font-family:'DM Sans',system-ui,sans-serif;color:#26262D;z-index:2147483647">
    <div style="max-width:360px;text-align:center;padding:32px;background:#fff;border:1px solid #E8E8F0;border-radius:16px;box-shadow:0 1px 2px rgba(15,15,17,.06)">
      <div style="font-size:16px;font-weight:700;color:#0F0F11;margin-bottom:6px">Workspace no longer open</div>
      <div style="font-size:13px;color:#52525F;line-height:1.5;margin-bottom:18px">This pane was editing a forecast in a Lodgeic tab that has moved on. Go back to the workspace and pop the pane out again.</div>
      <button onclick="window.close()" style="height:36px;padding:0 16px;border-radius:10px;border:none;background:var(--fv3-accent, #FF7A00);color:#fff;font-size:13px;font-weight:600;cursor:pointer">Close window</button>
    </div>
  </div>`;

function mirrorHead(parentDoc: Document, childDoc: Document): () => void {
  const copied = new WeakMap<Node, Node>();
  const copy = (node: Node) => {
    if (!(node instanceof HTMLElement)) return;
    const tag = node.tagName;
    const isStyle = tag === 'STYLE' || (tag === 'LINK' && /stylesheet|preconnect|preload/i.test((node as HTMLLinkElement).rel));
    if (!isStyle || copied.has(node)) return;
    const clone = node.cloneNode(true) as HTMLElement;
    // Dev-server <style> tags mutate their text on hot reload — re-read then.
    childDoc.head.appendChild(clone);
    copied.set(node, clone);
  };
  Array.from(parentDoc.head.children).forEach(copy);
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      m.addedNodes.forEach(copy);
      m.removedNodes.forEach((n) => { const c = copied.get(n); if (c && c.parentNode) c.parentNode.removeChild(c); });
      if (m.type === 'characterData' || m.type === 'childList') {
        const target = m.target.nodeType === Node.TEXT_NODE ? m.target.parentNode : m.target;
        if (target && target instanceof HTMLStyleElement) {
          const c = copied.get(target) as HTMLStyleElement | undefined;
          if (c) c.textContent = target.textContent;
        }
      }
    });
  });
  observer.observe(parentDoc.head, { childList: true, subtree: true, characterData: true });
  return () => observer.disconnect();
}

// Root attributes the stylesheets key on (theme, shell, palettes). Copied up
// front and kept in sync, so toggling dark mode or the layout while the pane
// is out restyles it too.
const MIRRORED_ROOT_ATTRS = ['data-theme', 'data-shell', 'data-chart-palette', 'data-sidebar-theme'];

function mirrorRootAttrs(from: HTMLElement, to: HTMLElement): () => void {
  const sync = () => {
    to.className = from.className;
    MIRRORED_ROOT_ATTRS.forEach((name) => {
      const v = from.getAttribute(name);
      // Classic panes never carried data-shell; keep them exactly as before.
      if (v == null || (name === 'data-shell' && v !== 'inset')) to.removeAttribute(name);
      else to.setAttribute(name, v);
    });
  };
  sync();
  const observer = new MutationObserver(sync);
  observer.observe(from, { attributes: true, attributeFilter: ['class', ...MIRRORED_ROOT_ATTRS] });
  return () => observer.disconnect();
}

const POPOUT_NAME = 'lodgeic-detail-pane';

function popoutFeatures(width: number, height: number): string {
  const left = Math.max(0, window.screenX + window.outerWidth - width - 24);
  const top = Math.max(0, window.screenY + 60);
  // `popup` (not `popup=yes`) is what current Chrome keys on; anything it
  // doesn't recognise as a popup request lands in a new TAB.
  return `popup,width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no,resizable=yes,scrollbars=yes`;
}

// A popup window is only granted a real window (not a tab) when opened
// synchronously inside the user's click — by the time React has flipped
// `open` and the effect runs, Chrome has consumed the activation. The toggle
// handler pre-opens the window here; the effect adopts it.
let preopened: Window | null = null;
export function preopenPopoutWindow(width = 480, height = 900): boolean {
  try {
    if (preopened && !preopened.closed) return true;
    preopened = window.open('', POPOUT_NAME, popoutFeatures(width, height));
    return !!preopened;
  } catch {
    preopened = null;
    return false;
  }
}

export function PopoutWindow({ open, title, width = 480, height = 900, onClosed, emptyState, children }: PopoutWindowProps) {
  const winRef = useRef<Window | null>(null);
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const onClosedRef = useRef(onClosed);
  onClosedRef.current = onClosed;

  useEffect(() => {
    if (!open) return;
    const win = (preopened && !preopened.closed)
      ? preopened
      : window.open('', POPOUT_NAME, popoutFeatures(width, height));
    preopened = null;
    if (!win) {
      onClosedRef.current();
      return;
    }
    winRef.current = win;
    (window as unknown as { __lodgeicPopout?: Window | null }).__lodgeicPopout = win;
    const doc = win.document;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title.replace(/</g, '&lt;')}</title></head><body></body></html>`);
    doc.close();
    const stopAttrs = mirrorRootAttrs(document.documentElement, doc.documentElement);
    doc.body.className = document.body.className;
    doc.body.style.margin = '0';
    doc.body.style.background = 'var(--lodge-surface, #fff)';
    const root = doc.createElement('div');
    root.id = 'popout-root';
    // forecast-v3 scope hook: New-look rules key off .fv3-root
    root.className = 'fv3-root';
    root.style.cssText = 'min-height:100vh;display:flex;flex-direction:column';
    doc.body.appendChild(root);
    const stopMirror = mirrorHead(document, doc);
    setContainer(root);

    let blocked = false;
    const block = () => {
      if (blocked || win.closed) return;
      blocked = true;
      try {
        root.remove();
        doc.body.insertAdjacentHTML('beforeend', BLOCK_HTML);
      } catch { /* window gone */ }
    };
    const closeChild = () => { try { if (!win.closed) win.close(); } catch { /* noop */ } };

    // Child closed by the user → dock.
    const poll = window.setInterval(() => {
      if (win.closed) {
        window.clearInterval(poll);
        setContainer(null);
        onClosedRef.current();
      }
    }, 400);
    win.addEventListener('pagehide', () => { /* handled by the poll — pagehide also fires on reload */ });
    // Parent navigated / refreshed / closed → no parent to write through.
    window.addEventListener('pagehide', closeChild);

    return () => {
      window.clearInterval(poll);
      window.removeEventListener('pagehide', closeChild);
      stopMirror();
      stopAttrs();
      setContainer(null);
      // Parent unmounted the popout (moved away / version closed): block,
      // don't silently leave stale editable UI in another window.
      block();
      winRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (winRef.current && !winRef.current.closed) winRef.current.document.title = title;
  }, [title]);

  // Grow for wide content, restore on release. Nested requests stack; the
  // window is restored when the last one releases. Only grows — never below
  // the size the user dragged it to.
  const wideDepth = useRef(0);
  const restoreSize = useRef<{ w: number; h: number } | null>(null);
  const requestWide = useCallback((minWidth: number, minHeight: number) => {
    const win = winRef.current;
    if (!win || win.closed) return () => {};
    if (wideDepth.current === 0) restoreSize.current = { w: win.outerWidth, h: win.outerHeight };
    wideDepth.current += 1;
    const targetW = Math.max(win.outerWidth, Math.min(minWidth, win.screen.availWidth - 40));
    const targetH = Math.max(win.outerHeight, Math.min(minHeight, win.screen.availHeight - 40));
    try {
      // Keep the window on screen when it grows to the right.
      const availLeft = (win.screen as Screen & { availLeft?: number }).availLeft ?? 0;
      const overflowX = win.screenX + targetW - (availLeft + win.screen.availWidth);
      if (overflowX > 0) win.moveBy(-overflowX, 0);
      win.resizeTo(targetW, targetH);
    } catch { /* blocked by the browser — the modal still fits via its vw cap */ }
    return () => {
      wideDepth.current = Math.max(0, wideDepth.current - 1);
      if (wideDepth.current === 0 && restoreSize.current && !win.closed) {
        try { win.resizeTo(restoreSize.current.w, restoreSize.current.h); } catch { /* noop */ }
        restoreSize.current = null;
      }
    };
  }, []);
  const sizeApi = useMemo<PopoutSizeApi>(() => ({ requestWide }), [requestWide]);

  if (!open || !container) return null;
  return createPortal(
    <PopoutSizeContext.Provider value={sizeApi}>
      <div className="flex-1 flex flex-col min-h-screen" style={{ fontFamily: 'inherit' }}>
        {children ?? emptyState ?? null}
      </div>
    </PopoutSizeContext.Provider>,
    container
  );
}
